import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, Radio, Tag } from 'antd'
import { LockOutlined } from '@ant-design/icons'
import type { AppFixedInfo, ListingCategory } from '@/types'
import { getOrg } from '@/api/org/main'
import { isMockSpaceId, resolveOwnerSpaceId } from '@/lib/currentOrg'
import { findSpaceById } from '@/lib/spaces'
import { useUserStore } from '@/store'
import { fillSelfBuiltOwnerSpace } from './formModel'
import { Field } from './parts'

export default function AppFixedInfoSection({
  value,
  editable,
  onChange,
}: {
  value: AppFixedInfo
  editable: boolean
  onChange: (next: AppFixedInfo) => void
}) {
  const { t } = useTranslation()
  const [spaceName, setSpaceName] = useState<string | undefined>()
  const valueRef = useRef(value)
  valueRef.current = value

  const orgId = resolveOwnerSpaceId(value.owner_space_id)
  const currentSpaceId = useUserStore((s) => s.space_id)
  const spaces = useUserStore((s) => s.spaces)
  const resolvedCurrentSpaceId = resolveOwnerSpaceId(currentSpaceId)
  const currentSpace = findSpaceById(
    spaces,
    resolvedCurrentSpaceId ?? currentSpaceId,
  )
  const currentSpaceName = currentSpace?.name

  // 全部空间可用的历史版本没有 space；切到空间自建时补当前空间
  useEffect(() => {
    if (!editable) return
    const latest = valueRef.current
    const next = fillSelfBuiltOwnerSpace(latest, {
      space_id: currentSpaceId,
      space_name: currentSpaceName,
    })
    if (
      next.owner_space_id === latest.owner_space_id &&
      next.owner_workspace === latest.owner_workspace
    ) {
      return
    }
    onChange(next)
    // onChange 不稳定；owner 写入后函数原样返回即停
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editable,
    value.distribution_type,
    value.owner_space_id,
    currentSpaceId,
    currentSpaceName,
  ])

  // 所属空间展示名 + 把 mock id 换成真实 org
  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    void getOrg(orgId)
      .then((org) => {
        if (cancelled || !org?.id) return
        const name = org.name?.trim() || org.id
        setSpaceName(name)
        const latest = valueRef.current
        if (
          editable &&
          (latest.owner_space_id !== org.id || latest.owner_workspace !== name)
        ) {
          // 只用最新表单打补丁，避免覆盖飞行期间用户改过的其它字段
          onChange({
            ...latest,
            owner_space_id: org.id,
            owner_workspace: name,
          })
        }
      })
      .catch(() => {
        if (!cancelled) setSpaceName(undefined)
      })
    return () => {
      cancelled = true
    }
    // 只在 org / 可编辑态变化时拉一次；onChange 成功后 id 对齐即停
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, editable, value.owner_space_id, value.owner_workspace])

  const toggleCategory = (cat: ListingCategory, checked: boolean) => {
    const next = checked
      ? [...value.listing_space_types, cat]
      : value.listing_space_types.filter((c) => c !== cat)
    if (next.length === 0) return
    onChange({ ...value, listing_space_types: next })
  }

  const ownerLabel = useMemo(() => {
    if (spaceName) return spaceName
    if (value.owner_workspace && !isMockSpaceId(value.owner_workspace)) {
      return value.owner_workspace
    }
    if (!orgId && value.distribution_type === 'self_built') {
      return currentSpaceName || resolvedCurrentSpaceId || '—'
    }
    return orgId || '—'
  }, [
    spaceName,
    value.owner_workspace,
    value.distribution_type,
    orgId,
    currentSpaceName,
    resolvedCurrentSpaceId,
  ])

  return (
    <div>
      <Field
        label={
          <span>
            {t('versionForm.distributionMethod')}
            {!editable && (
              <LockOutlined
                style={{ marginLeft: 6, color: 'rgba(0,0,0,0.3)' }}
              />
            )}
          </span>
        }
        required
        hint={!editable ? t('versionForm.fixedInfoLocked') : undefined}
      >
        <Radio.Group
          value={value.distribution_type}
          disabled={!editable}
          onChange={(e) => {
            const nextType = e.target.value as AppFixedInfo['distribution_type']
            onChange({ ...value, distribution_type: nextType })
          }}
        >
          <Radio value="self_built">
            {t('versionForm.distSpacePrivate')}
          </Radio>
          <Radio value="all_spaces">{t('versionForm.distPublic')}</Radio>
        </Radio.Group>
      </Field>

      {value.distribution_type === 'self_built' ? (
        <Field label={t('versionForm.ownerWorkspace')} style={{ marginBottom: 0 }}>
          <Tag>{ownerLabel}</Tag>
        </Field>
      ) : (
        <Field
          label={t('versionForm.listingCategory')}
          required
          style={{ marginBottom: 0 }}
        >
          <Checkbox
            disabled={!editable}
            checked={value.listing_space_types.includes('workspace')}
            onChange={(e) => toggleCategory('workspace', e.target.checked)}
          >
            {t('versionForm.catWorkspace')}
          </Checkbox>
          <Checkbox
            disabled={!editable}
            checked={value.listing_space_types.includes('personal')}
            onChange={(e) => toggleCategory('personal', e.target.checked)}
          >
            {t('versionForm.catPersonal')}
          </Checkbox>
        </Field>
      )}
    </div>
  )
}
